/**
 * Login page: centered card, i18n by browser language (fallback English).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { signIn } from '../modules/api';
import { getTechWebUrl } from '../config/runtimeConfig';
import LanguageIconDropdown from './shared-theme/LanguageIconDropdown';
import ColorModeIconDropdown from './shared-theme/ColorModeIconDropdown';

export function TemplateSignIn() {
  const { t } = useTranslation();
  const [login, setLogin] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await signIn(login, password);
      const token = res?.data?.token;
      const user = res?.data?.user || {};
      const role = user?.roles?.[0]?.role || '';
      const email = user?.email || login;

      if (!token) {
        throw new Error('No token in response');
      }

      localStorage.setItem('token', token);
      localStorage.setItem('role', role);
      localStorage.setItem('Email', email);

      const techWebUrl = getTechWebUrl();
      if (role === 'TECHNICIAN' && techWebUrl) {
        window.location.href = techWebUrl;
        return;
      }

      window.location.hash = role === 'TECHNICIAN' ? '#/service/tickets' : '#/';
    } catch (err) {
      setError(t('signIn.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': {
          minHeight: '100dvh',
        },
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        boxSizing: 'border-box',
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          position: 'fixed',
          top: { xs: 12, sm: 16 },
          right: { xs: 12, sm: 16 },
          zIndex: 2,
        }}
      >
        <LanguageIconDropdown />
        <ColorModeIconDropdown />
      </Stack>

      <Card
        elevation={1}
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 3,
          mx: 'auto',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 }, '&:last-child': { pb: { xs: 3, sm: 4 } } }}>
          <Stack alignItems="center" spacing={1} sx={{ mb: 3, textAlign: 'center' }}>
            <LockOutlinedIcon color="primary" sx={{ fontSize: 36 }} />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
              {t('signIn.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('signIn.subtitle')}
            </Typography>
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box
            component="form"
            onSubmit={onSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              width: '100%',
            }}
          >
            <TextField
              fullWidth
              label={t('signIn.email')}
              autoComplete="username"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label={t('signIn.password')}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button type="submit" variant="contained" fullWidth disabled={loading} size="large">
              {loading ? t('signIn.submitting') : t('signIn.submit')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
